import { lazy, Suspense, useState, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { AuthProvider, DataProvider, ActiveHouseProvider, useActiveHouse, useAuth } from './contexts/index';
import { PlatformConfigProvider } from './contexts/PlatformConfigContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { SessionIdleGuard } from './ui/components/SessionIdleGuard';
import { AndroidUpdatePrompt } from './ui/components/AndroidUpdatePrompt';
import { DataLoadingFallback } from './ui/components/DataLoadingFallback';
import { Splash } from './ui/screens/auth/Splash';
import { useTranslation, ensureLocale } from './i18n/index';
import { usePalette, useNetworkStatus, usePersistedLocale, useAndroidAppUpdate } from './hooks/index';
import { isAppVersionSupported } from './core/config/appVersion';
import { APP_VERSION } from './config/appVersion';
import { readRemoteConfigFlags } from './services/firebase/remoteConfigFlags';
import { parseLegalPath, legalPathFor, type LegalDocId } from './core/legal/termsVersion';
import { isAuthActionPath } from './core/auth/authAction';
import { MaintenanceGuard } from './guards/index';
import { importWithRetry, prefetchRoleOverview } from './app/screenLoaders';
import type { AppUser, Language } from './types';

const Login = lazy(() =>
  importWithRetry(() => import('./ui/screens/auth/Login')).then((m) => ({ default: m.Login })),
);
const LoginUpdateRequired = lazy(() =>
  importWithRetry(() => import('./ui/screens/auth/LoginUpdateRequired')).then((m) => ({
    default: m.LoginUpdateRequired,
  })),
);
const AppShell = lazy(() =>
  importWithRetry(() => import('./app/AppShell')).then((m) => ({ default: m.AppShell })),
);
const LegalDocumentScreen = lazy(() =>
  importWithRetry(() => import('./ui/screens/legal/LegalDocumentScreen')).then((m) => ({
    default: m.LegalDocumentScreen,
  })),
);
const AuthActionScreen = lazy(() =>
  importWithRetry(() => import('./ui/screens/auth/AuthActionScreen')).then((m) => ({
    default: m.AuthActionScreen,
  })),
);

type AppStage = 'splash' | 'login' | 'app';

/** Cover splash enter + number sequence before routing to login/app. */
const SPLASH_MIN_MS = 1_200;
/** Never freeze on splash if Auth restore hangs (Safari iOS). */
const SPLASH_MAX_MS = 8_000;

function BootFallback() {
  return <DataLoadingFallback />;
}

function DataScope({ children, lang }: { children: ReactNode; lang: Language }) {
  const { activeHouseId } = useActiveHouse();
  // Track last house in state so the switch flag is computed during render (no refs).
  const [trackedHouseId, setTrackedHouseId] = useState<string | undefined>(undefined);
  const contextSwitch = trackedHouseId !== undefined && trackedHouseId !== activeHouseId;

  useLayoutEffect(() => {
    setTrackedHouseId(activeHouseId);
  }, [activeHouseId]);

  return (
    <DataProvider
      key={activeHouseId || '__global__'}
      houseId={activeHouseId}
      lang={lang}
      contextSwitch={contextSwitch}
    >
      <PlatformConfigProvider>
        <SubscriptionProvider>
          <SessionIdleGuard />
          {children}
        </SubscriptionProvider>
      </PlatformConfigProvider>
    </DataProvider>
  );
}

function AppRoutes() {
  const { user, login, logout, authReady } = useAuth();
  const [stage, setStage] = useState<AppStage>('splash');
  const [splashMinElapsed, setSplashMinElapsed] = useState(false);
  const [splashTimedOut, setSplashTimedOut] = useState(false);
  const { lang, setLang, theme, setTheme } = usePersistedLocale(user?.id ?? null);
  const { online, networkOnline, serverReachable, simulateOffline, setSimulateOffline } = useNetworkStatus();
  const [pathLegalDoc, setPathLegalDoc] = useState<LegalDocId | null>(() =>
    typeof window !== 'undefined' ? parseLegalPath(window.location.pathname) : null,
  );
  const [authActionOpen, setAuthActionOpen] = useState(() =>
    typeof window !== 'undefined' && isAuthActionPath(window.location.pathname),
  );

  const t = useTranslation(lang);
  const p = usePalette(theme);
  const androidUpdate = useAndroidAppUpdate();

  const androidUpdateBusy = androidUpdate.phase === 'preparing'
    || androidUpdate.phase === 'downloading'
    || androidUpdate.phase === 'installing';

  const androidUpdateOverlay = androidUpdate.visible && androidUpdate.offer ? (
    <AndroidUpdatePrompt
      t={t}
      offer={androidUpdate.offer}
      phase={androidUpdate.phase}
      force={androidUpdate.force}
      errorKey={androidUpdate.errorKey}
      onUpdate={() => { void androidUpdate.update(); }}
      onLater={androidUpdate.dismiss}
    />
  ) : null;

  useEffect(() => {
    void ensureLocale(lang);
  }, [lang]);

  useEffect(() => {
    document.title = t.documentTitle ?? 'Alea-Tui';
  }, [t.documentTitle]);

  useEffect(() => {
    const onPopState = () => {
      setPathLegalDoc(parseLegalPath(window.location.pathname));
      setAuthActionOpen(isAuthActionPath(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const minId = window.setTimeout(() => setSplashMinElapsed(true), SPLASH_MIN_MS);
    const maxId = window.setTimeout(() => setSplashTimedOut(true), SPLASH_MAX_MS);
    return () => {
      window.clearTimeout(minId);
      window.clearTimeout(maxId);
    };
  }, []);

  useEffect(() => {
    // Warm login chunk while splash/auth resolve so the first paint is not empty.
    void import('./ui/screens/auth/Login');
  }, []);

  useEffect(() => {
    if (!user || stage === 'splash') return;
    prefetchRoleOverview(user.role);
    setStage('app');
  }, [user, stage]);

  useEffect(() => {
    if (stage !== 'splash') return;
    const ready = (authReady && splashMinElapsed) || splashTimedOut;
    if (!ready) return;
    if (user) prefetchRoleOverview(user.role);
    setStage(user ? 'app' : 'login');
  }, [stage, user, authReady, splashMinElapsed, splashTimedOut]);

  const handleLogin = async (sessionUser: AppUser) => {
    prefetchRoleOverview(sessionUser.role);
    const ok = await login(sessionUser);
    if (ok) setStage('app');
  };

  const handleLogout = async () => {
    await logout();
    setStage('login');
  };

  if (authActionOpen) {
    return (
      <div className="app-root">
        <Suspense fallback={<BootFallback />}>
          <AuthActionScreen
            p={p}
            t={t}
            lang={lang}
            setLang={setLang}
            onGoToLogin={() => {
              setAuthActionOpen(false);
              if (window.location.pathname !== '/') {
                window.history.replaceState(null, '', '/');
              }
              setStage('login');
            }}
          />
        </Suspense>
      </div>
    );
  }

  // Legal docs: wait for auth so logged-in users get in-shell content, not a separate page.
  if (pathLegalDoc) {
    if (stage === 'splash' || !authReady) {
      return <Splash p={p} lang={lang} />;
    }
    if (!user) {
      return (
        <div className="app-root">
          <Suspense fallback={<BootFallback />}>
            <LegalDocumentScreen
              doc={pathLegalDoc}
              p={p}
              t={t}
              lang={lang}
              setLang={setLang}
              variant="standalone"
            />
          </Suspense>
        </div>
      );
    }
  }

  if (stage === 'splash') {
    return <Splash p={p} lang={lang} />;
  }

  if (!user) {
    const remote = readRemoteConfigFlags();
    const updateRequired = !isAppVersionSupported(APP_VERSION, remote.minAppVersion);

    return (
      <div className="app-root">
        <DataProvider houseId="" lang={lang}>
          <PlatformConfigProvider>
            <Suspense fallback={<BootFallback />}>
              {updateRequired ? (
                <LoginUpdateRequired
                  t={t}
                  currentVersion={APP_VERSION}
                  minVersion={remote.minAppVersion}
                  busy={androidUpdateBusy}
                  errorKey={androidUpdate.errorKey}
                  onUpdate={androidUpdate.offer
                    ? () => { void androidUpdate.update(); }
                    : undefined}
                />
              ) : (
                <Login
                  p={p}
                  t={t}
                  lang={lang}
                  setLang={setLang}
                  theme={theme}
                  setTheme={setTheme}
                  onLogin={handleLogin}
                />
              )}
            </Suspense>
          </PlatformConfigProvider>
        </DataProvider>
        {androidUpdateOverlay}
      </div>
    );
  }

  return (
    <div className="app-root">
      <ActiveHouseProvider user={user}>
        <DataScope lang={lang}>
          <MaintenanceGuard user={user} onBlocked={handleLogout} />
          <Suspense fallback={<BootFallback />}>
            <AppShell
              p={p}
              t={t}
              lang={lang}
              setLang={setLang}
              theme={theme}
              setTheme={setTheme}
              user={user}
              online={online}
              networkOnline={networkOnline}
              serverReachable={serverReachable}
              simulateOffline={simulateOffline}
              onToggleSimulateOffline={() => setSimulateOffline(!simulateOffline)}
              initialLegalDoc={pathLegalDoc}
              onLegalDocChange={(doc) => {
                setPathLegalDoc(doc);
                const nextPath = doc ? legalPathFor(doc) : '/';
                if (window.location.pathname !== nextPath) {
                  window.history.pushState(null, '', nextPath);
                }
              }}
            />
          </Suspense>
        </DataScope>
      </ActiveHouseProvider>
      {androidUpdateOverlay}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
